import { useEffect, useMemo } from "react";
import { DollarSign } from "lucide-react";
import styles from "./RevenueRealityReportViewer.module.css";
import { REVENUE_REALITY_TAB_CONFIG } from "./RevenueRealityTabConfig";
import {
  GenericFunnelViewer,
  FunnelChannelRealOrders,
  FunnelReturnsRefunds,
  FunnelUnitEconomics,
  FunnelCacPayback,
  FunnelRepeatCustomers,
  FunnelScaleSpend,
  FunnelRevenueDiversification,
  FunnelDiscountDependency,
  FunnelRepeatabilityScore,
  FunnelFixedCostViability,
  FunnelProductSkuEconomics,
  FunnelCustomerLifetimeEconomics,
  FunnelGrowthEfficiency,
  FunnelMarginStability,
  FunnelOperationalReadiness,
} from "../assessmentViewer/FunnelStage";

const asText = (value) => {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object" && typeof value.text === "string") {
    return value.text.trim();
  }
  if (value && typeof value === "object" && typeof value.report_body === "string") {
    return value.report_body.trim();
  }
  if (value && typeof value === "object" && Array.isArray(value.report_body)) {
    return value.report_body
      .map((s) => (typeof s === "string" ? s.trim() : ""))
      .filter(Boolean)
      .join("\n\n");
  }
  return "";
};

/* ─── Revenue Reality sub-report keys → dedicated viewer components ─── */
const REVENUE_REALITY_SUB_KEYS = [
  { key: "returns_refunds", label: "Returns & Refunds", Viewer: FunnelReturnsRefunds },
  { key: "unit_economics", label: "Unit Economics", Viewer: FunnelUnitEconomics },
  { key: "customer_acquisition_health", label: "Customer Acquisition Health", Viewer: FunnelCacPayback },
  { key: "channels_bring_real_orders", label: "Channels Bring Real Orders", Viewer: FunnelChannelRealOrders },
  { key: "repeat_purchases", label: "Repeat Purchases", Viewer: FunnelRepeatCustomers },
  { key: "scale_spend", label: "Scale Spend", Viewer: FunnelScaleSpend },
  { key: "diversification", label: "Diversification", Viewer: FunnelRevenueDiversification },
  { key: "margin_discount", label: "Margin & Discount", Viewer: FunnelDiscountDependency },
  { key: "repeatability_score", label: "Repeatability Score", Viewer: FunnelRepeatabilityScore },
];

/* ─── Standalone tab keys → dedicated viewer components ─── */
const STANDALONE_VIEWER_MAP = {
  fixed_cost_viability: { Viewer: FunnelFixedCostViability, label: "Fixed Cost Viability" },
  product_sku_economics: { Viewer: FunnelProductSkuEconomics, label: "Product SKU Economics" },
  customer_lifetime_economics: { Viewer: FunnelCustomerLifetimeEconomics, label: "Customer Lifetime Economics" },
  growth_efficiency_channel_mix: { Viewer: FunnelGrowthEfficiency, label: "Growth Efficiency & Channel Mix" },
  margin_stability: { Viewer: FunnelMarginStability, label: "Margin Stability & Risk" },
  operations_readiness_confidence: { Viewer: FunnelOperationalReadiness, label: "Operational Readiness & Data Confidence" },
};

const FREE_LOCKED_REVENUE_TABS = new Set([
  "customer_lifetime_economics",
  "growth_efficiency_channel_mix",
  "margin_stability",
  "operations_readiness_confidence",
]);

const LOCKED_TAB_TEASERS = {
  customer_lifetime_economics:
    "See how much long-term customer value your current acquisition strategy is really creating.",
  growth_efficiency_channel_mix:
    "See which channels are creating efficient growth and where your spend is leaking margin.",
  margin_stability:
    "See how resilient your margins are under discount pressure, cost shifts, and sales volatility.",
  operations_readiness_confidence:
    "See whether your systems, reporting, and operating rhythm are strong enough to support scale.",
};

const SectionHeader = ({ title }) => (
  <div className={styles.sectionHeaderBar}>
    <h2 className={styles.sectionHeaderText}>{title}</h2>
  </div>
);

const RevenueRealityReportViewer = ({
  data = {},
  activeTab = "revenue_reality",
  isMonthlySubscription = false,
}) => {
  const isLockedForFree =
    !isMonthlySubscription && FREE_LOCKED_REVENUE_TABS.has(activeTab);
  const unlockedSectionCount = isMonthlySubscription ? Infinity : 2;
  const sections = useMemo(() => {
    if (activeTab === "revenue_reality") {
      const revenueData = data?.revenue_reality || {};
      const assessmentText =
        asText(revenueData?.revenue_reality_assessment) ||
        asText(data?.revenue_reality_assessment);

      const reportSections = REVENUE_REALITY_SUB_KEYS
        .map((sub) => {
          const text = asText(revenueData[sub.key]);
          return text ? { ...sub, text } : null;
        })
        .filter(Boolean);

      if (assessmentText) {
        return [
          {
            key: "revenue_reality_assessment",
            label: "Revenue Reality Assessment",
            text: assessmentText,
            Viewer: GenericFunnelViewer,
            isAssessment: true,
            viewerProps: { title: null },
          },
          ...reportSections,
        ];
      }

      return reportSections;
    }
    return [];
  }, [data, activeTab]);

  const activeTabLabel = useMemo(() => {
    if (activeTab === "revenue_reality") return "Revenue Reality";
    const standalone = STANDALONE_VIEWER_MAP[activeTab];
    if (standalone) return standalone.label;
    const tab = REVENUE_REALITY_TAB_CONFIG.find((t) => t.key === activeTab);
    return tab?.label || "Report";
  }, [activeTab]);
  const lockedTabTeaser = LOCKED_TAB_TEASERS[activeTab] || "";

  // Revenue Reality tab
  if (activeTab === "revenue_reality") {
    if (!sections.length) {
      return (
        <div className={styles.page}>
          <div className={styles.header}>
            <div className={styles.headerIcon}><DollarSign /></div>
            <h1 className={styles.headerTitle}>Revenue Reality Report</h1>
          </div>
          <div className={styles.content}>
            <p className={styles.emptyState}>No Revenue Reality data available yet.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerIcon}><DollarSign /></div>
          <h1 className={styles.headerTitle}>Revenue Reality Report</h1>
        </div>
        <div className={styles.scrollContent}>
          <div className={styles.content}>
            {sections.map((section, idx) => {
              const Viewer = section.Viewer;
              const isSectionLocked = idx >= unlockedSectionCount;
              return (
                <section key={section.key} className={styles.section}>
                  <SectionHeader title={`${idx + 1}. ${section.label}`} />
                  {isSectionLocked ? (
                    <div className={styles.sectionBody}>
                      <div className={styles.lockedCard}>
                        <span className={styles.lockedBadge}>Monthly plan</span>
                        <h2 className={styles.lockedTitle}>Upgrade to unlock this section</h2>
                        <p className={styles.lockedBody}>
                          This part of Revenue Reality is available on the Monthly Subscription
                          plan.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.sectionBody}>
                      <Viewer markdown={section.text} {...(section.viewerProps || {})} />
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Standalone tabs
  if (isLockedForFree) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerIcon}><DollarSign /></div>
          <h1 className={styles.headerTitle}>{activeTabLabel}</h1>
        </div>
        <div className={styles.content}>
          <div className={styles.lockedCard}>
            <span className={styles.lockedBadge}>Monthly plan</span>
            <h2 className={styles.lockedTitle}>Unlock {activeTabLabel}</h2>
            <p className={styles.lockedBody}>
              {lockedTabTeaser ||
                "This Revenue Reality tab is available on the Monthly Subscription plan."}
            </p>
            <p className={styles.lockedBody}>
              This assessment is available on the Monthly Subscription plan. Upgrade to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const sectionData = data?.[activeTab];
  const text = asText(sectionData);
  const standaloneEntry = STANDALONE_VIEWER_MAP[activeTab];
  const StandaloneViewer = standaloneEntry?.Viewer || GenericFunnelViewer;

  if (!text) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerIcon}><DollarSign /></div>
          <h1 className={styles.headerTitle}>{activeTabLabel}</h1>
        </div>
        <div className={styles.content}>
          <p className={styles.emptyState}>
            No {activeTabLabel.toLowerCase()} data available yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><DollarSign /></div>
        <h1 className={styles.headerTitle}>{activeTabLabel}</h1>
      </div>
      <div className={styles.scrollContent}>
        <div className={styles.content}>
          <section className={styles.section}>
            <div className={styles.sectionBody}>
              <StandaloneViewer markdown={text} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RevenueRealityReportViewer;
